import { useMemo } from 'react';
import { useCustomInfiniteQuery } from './useCustomQuery';
import { searchApi } from '../api/endpoints';
import { DEFAULT_QUERY_CACHE_STALE_TIME } from '@sizzlenz/shared';
import type { SearchQuery, SearchResponse } from '../api/types';

export const SEARCH_QUERY_KEY = 'search';

/**
 * Hook for infinite/cursor-based search queries
 *
 * @example
 * ```tsx
 * const { data, fetchNextPage, hasNextPage, isFetchingNextPage, pagination } = useSearch({
 *   query: 'chocolate',
 *   objectType: 'item',
 *   limit: 20,
 * });
 *
 * // Access all pages
 * const allResults = data?.pages.flatMap(page => page.results) ?? [];
 *
 * // Access pagination info from last page
 * console.log(pagination.nextCursor, pagination.prevCursor);
 * ```
 */
export function useSearch(params: Omit<SearchQuery, 'nextCursor' | 'prevCursor'>) {
  const {
    query,
    objectType,
    ownerType,
    ownerId,
    tags,
    tagsMatch,
    active,
    createdAfter,
    createdBefore,
    updatedAfter,
    updatedBefore,
    systemData,
    sortBy,
    limit = 20,
    sessionHash,
    includeEngagement,
    enabled = true,
    following,
    connectionFromType,
    connectionFromId,
  } = params;
  const result = useCustomInfiniteQuery<SearchResponse>({
    queryKey: [
      SEARCH_QUERY_KEY,
      query,
      objectType,
      ownerType,
      ownerId,
      tags,
      tagsMatch,
      active,
      createdAfter,
      createdBefore,
      updatedAfter,
      updatedBefore,
      systemData,
      sortBy,
      limit,
      sessionHash,
      includeEngagement,
      following,
      connectionFromType,
      connectionFromId,
    ],
    queryFn: ({ pageParam }) =>
      searchApi.search({
        ...params,
        nextCursor: pageParam as string | undefined,
      }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.pagination.nextCursor ?? undefined,
    enabled,
    staleTime: DEFAULT_QUERY_CACHE_STALE_TIME,
  });

  // Extract pagination info from the last page for backward compatibility
  const pagination = useMemo(() => {
    const lastPage = result.data?.pages[result.data.pages.length - 1];
    return lastPage?.pagination || { limit, nextCursor: null, prevCursor: null };
  }, [result.data?.pages, limit]);

  return {
    ...result,
    pagination,
  };
}
